/**
 * Chart tokens — цвета и геометрия графика.
 *
 * Всё возвращается CSS-переменными, а не значениями: `var()` резолвится и в
 * презентационных SVG-атрибутах (`stroke`, `fill`, `stop-color`), поэтому
 * график флипается вместе с `data-color-scheme` так же бесплатно, как любой
 * другой компонент системы — без резолвера, без ре-рендера, без второго
 * набора «тёмных» пропов.
 *
 * @format
 */

import {surfaceVar} from 'ui/theme/surfaceContext';

/** Слоты серий. Порядок фиксирован и НЕ зациклен — см. `seriesVar`. */
export const SERIES_SLOTS = [
  'series1',
  'series2',
  'series3',
  'series4',
  'series5',
  'series6',
] as const;
export type SeriesSlot = (typeof SERIES_SLOTS)[number];

export const MAX_SERIES = SERIES_SLOTS.length;

/**
 * Оценочные роли, доступные серии.
 *
 * Обычной серии они не положены — цвет серии называет, ЧТО измерено, а не как
 * дела. Но есть графики, где оценка и есть предмет: найденное против
 * закрытого, просроченное против сделанного. Там красное обязано быть тем же
 * красным, что и на бейджах рядом, иначе читатель решит, что это разные
 * «плохо».
 */
export const SERIES_STATUS_ROLES = ['good', 'bad', 'alert', 'primary'] as const;
export type SeriesStatusRole = (typeof SERIES_STATUS_ROLES)[number];

/**
 * Другие ступени той же роли: приглушённая (`good-container`), осветлённая
 * (`good-hover`), насыщенная (`good-active`).
 *
 * Нужны там, где одна роль работает сразу двумя тонами: заливка слоя и его
 * кромка, фон и подсветка. Брать для этого соседнюю роль нельзя — цвет тут же
 * начнёт означать другую сущность.
 */
export const SERIES_ROLE_TONES = ['container', 'hover', 'active'] as const;
export type SeriesRoleTone = (typeof SERIES_ROLE_TONES)[number];
export type SeriesStatusTone = `${SeriesStatusRole}-${SeriesRoleTone}`;

/**
 * Нейтральный — цвет не величины, а обстоятельства.
 *
 * Им красится то, что на графике объясняет фон: подложка выходного дня,
 * зона, в которой измерений не было. Слот палитры такому смыслу не годится —
 * он поставил бы обстоятельство в один ряд с измерениями и заставил искать
 * ему место в легенде; оценочная роль не годится тем более: выходной день не
 * «хорошо» и не «плохо».
 */
export const NEUTRAL_COLOR = 'neutral';

export type SeriesColor =
  | SeriesSlot
  | 'forecast'
  | SeriesStatusRole
  | SeriesStatusTone
  | typeof NEUTRAL_COLOR;

/** `chartVar('series1')` → `'var(--ads-palette-chart-series1)'`. */
export const chartVar = (key: SeriesSlot | 'forecast'): string =>
  `var(--ads-palette-chart-${key})`;

const isStatusRole = (color: string): color is SeriesStatusRole =>
  (SERIES_STATUS_ROLES as readonly string[]).includes(color);

/** Разворачивает значение `series.color` в CSS-переменную. */
export const colorVar = (color: SeriesColor): string => {
  if (color === NEUTRAL_COLOR) return surfaceVar('onSurfaceMuted');
  if (isStatusRole(color)) return `var(--ads-palette-${color}-main)`;

  for (const tone of SERIES_ROLE_TONES) {
    const suffix = `-${tone}`;
    if (!color.endsWith(suffix)) continue;
    const role = color.slice(0, -suffix.length);
    if (isStatusRole(role)) return `var(--ads-palette-${role}-${tone})`;
  }

  return chartVar(color as SeriesSlot | 'forecast');
};

/**
 * Цвет серии по её порядковому номеру.
 *
 * Седьмая серия не получает сгенерированный оттенок и не начинает палитру
 * заново: цвет, уже занятый первой серией, во второй раз означал бы, что
 * читатель различает их по чему-то другому — а различать нечем. Возвращаем
 * приглушённый серый (как у прогноза) и предупреждаем в разработке: правильный
 * ответ на «серий больше шести» — свернуть хвост в «Прочее» или разложить на
 * несколько графиков, и это решение принимает страница, а не компонент.
 */
export function seriesVar(index: number): string {
  if (index < MAX_SERIES) return chartVar(SERIES_SLOTS[index]);
  if (!import.meta.env.PROD) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Chart] Серия №${index + 1}: в палитре ${MAX_SERIES} различимых цветов. ` +
        'Сверните хвост в «Прочее» или разбейте на несколько графиков — ' +
        'седьмой цвет читатель уже не отличит от первого.',
    );
  }
  return chartVar('forecast');
}

// ── Хром графика ───────────────────────────────────────────────────────
//
// Сетка, оси и подписи берутся из surface-рампы, а не из собственных
// токенов: это тот же самый «фон и текст на нём», что и во всей системе, и
// заводить им отдельные значения значило бы завести им отдельную судьбу.

export const GRID_COLOR = 'var(--ads-palette-surface-surface2)';
export const AXIS_COLOR = 'var(--ads-palette-surface-surface2)';
export const TICK_COLOR = 'var(--ads-palette-surface-onSurfaceMuted)';
/**
 * Подсветка дня под курсором — контрастная к поверхности и полупрозрачная.
 *
 * `inverseSurface` вместо буквального белого: на тёмной карточке он и есть
 * белый, а на светлой становится тёмным. Прописанный явным цветом белый в
 * светлой теме оказался бы подсветкой цвета фона — то есть её отсутствием.
 */
export const CURSOR_COLOR = 'var(--ads-palette-surface-inverseSurface)';
export const CURSOR_BAND_OPACITY = 0.2;
export const CURSOR_LINE_OPACITY = 0.35;

// ── Геометрия ──────────────────────────────────────────────────────────
//
// Линия тонкая: у графика работа — показывать форму, а не заявлять о себе
// весом. Точка появляется только под курсором и делается заметно крупнее
// линии, иначе в неё невозможно попасть.

export const STROKE_WIDTH = 2;

/** Ширина оси Y — вычитается из ширины поля при расчёте колонки дня. */
export const Y_AXIS_WIDTH = 56;

/**
 * Запас справа от поля построения — чтобы последняя подпись оси и активная
 * точка не срезались краем канвы.
 *
 * Вынесено в токен, потому что этим полем меряют не только сам график:
 * подписи, которые страница кладёт ПОД осью (границы периода), обязаны
 * вставать по краям поля построения, а не контейнера.
 */
export const PLOT_RIGHT_MARGIN = 8;

/** Радиус свечения у `series.glow`. */
export const GLOW_BLUR = 5;

/**
 * Насыщенная ступень той же роли — для кромок и активных точек.
 *
 * Точка под курсором обязана быть светлее своей заливки: она появляется
 * ПОВЕРХ неё, и одинаковый цвет означал бы, что её попросту не видно.
 * У серий из категориальной палитры `active`-ступени нет, там цвет остаётся
 * своим.
 */
export const activeVar = (color: SeriesColor): string => {
  const role = isStatusRole(color)
    ? color
    : SERIES_ROLE_TONES.map(tone => color.replace(new RegExp(`-${tone}$`), '')).find(isStatusRole);

  return role ? `var(--ads-palette-${role}-active)` : colorVar(color);
};
export const ACTIVE_DOT_RADIUS = 4;

/**
 * Границы радиуса активной точки, когда он выводится из ширины колонки.
 *
 * Точка шириной в день попадает в тон подсветке-полосе под ней — читается как
 * «этот день целиком». Но на очень плотном ряде она превратилась бы в
 * невидимую крупинку, а на коротком — в блин поверх соседей.
 */
export const ACTIVE_DOT_MIN = 1.75;
export const ACTIVE_DOT_MAX = 9;
export const DASH_FORECAST = '5 5';
export const GRID_DASH = '2 4';

/** Заливка области под линией — градиент от цвета серии к прозрачному. */
export const AREA_GRADIENT_STOPS = [
  {offset: '0%', opacity: 0.28},
  {offset: '100%', opacity: 0},
] as const;

/**
 * Спад цвета к нижней кромке у `fill: 'shaded'` — доля цвета серии, дожившая
 * до низа.
 *
 * Подмешивается ФОН, а не чёрный: чистый чёрный тянет любой оттенок в
 * «пыльный», особенно уже приглушённый `*-container`. С фоном слой выглядит
 * уходящим в глубину карточки, а верхняя кромка — та, по которой считывается
 * величина, — остаётся самой чистой.
 *
 * И не прозрачностью: на тёмной поверхности она затемняет, на светлой —
 * осветляет, то есть один приём дал бы в двух темах противоположный результат.
 */
export const SHADED_BOTTOM_MIX = 58;

/** `var(--x)` → `color-mix(in srgb, var(--x) 58%, <фон карточки>)`. */
export const shadeBottom = (color: string): string =>
  `color-mix(in srgb, ${color} ${SHADED_BOTTOM_MIX}%, var(--ads-palette-background-default))`;

/**
 * Высота по умолчанию. `ResponsiveContainer` в Recharts 2.x меряет родителя,
 * поэтому какая-то определённая высота нужна всегда — иначе график схлопнется
 * в ноль и молча не отрисуется.
 */
export const DEFAULT_HEIGHT = 240;
